import type { AttributeScope } from '@/services/db/data-index/entity';
import type { DataIndexExportContent, DataIndexExportTarget } from '@/services/db/data-index/exports';

/**
 * What the data index offers to export, and what each choice is called.
 *
 * Every family can be exported twice: the rows on their own, and the rows with
 * every campaign, segment and template that references them. The second is far
 * more expensive to build, which is why the backend keeps it as its own export
 * rather than as columns on the first.
 */

/** Which rows an export covers, before choosing rows or usage. */
type ExportFamily = { family: 'events' } | { family: 'attributes'; scope: AttributeScope };

/** One entry in the export menu. */
export type DataIndexExportChoice = {
  label: string;
  target: DataIndexExportTarget;
};

/** One object type's exports, which come in two families of their own. */
export type DataIndexExportObjectGroup = {
  objectTypeName: string;
  attributes: DataIndexExportChoice[];
  relationships: DataIndexExportChoice[];
};

/** Everything the menu offers, in the order it is shown. */
export type DataIndexExportMenu = {
  profile: DataIndexExportChoice[];
  objects: DataIndexExportObjectGroup[];
  events: DataIndexExportChoice[];
};

/** What the analytics event calls each attribute family. */
const ATTRIBUTE_EXPORT_NAMES = {
  profile: 'attributes',
  object: 'object_attributes',
  relationship: 'object_relationship_attributes',
} satisfies Record<AttributeScope['kind'], string>;

/**
 * What one export is called in the analytics event. The names are the
 * backend's, so one report spans both apps while they both serve this page.
 */
export function dataIndexExportAnalyticsType(target: DataIndexExportTarget): string {
  const family = target.family === 'events' ? 'events' : ATTRIBUTE_EXPORT_NAMES[target.scope.kind];
  return target.content === 'usage' ? `${family}_usage` : family;
}

function targetFor(family: ExportFamily, content: DataIndexExportContent): DataIndexExportTarget {
  return family.family === 'events'
    ? { family: 'events', content }
    : { family: 'attributes', scope: family.scope, content };
}

/** The pair of choices one family offers: the rows, then the rows with their usage. */
function choicePair(label: string, family: ExportFamily): DataIndexExportChoice[] {
  return [
    { label, target: targetFor(family, 'rows') },
    { label: `${label} Usage`, target: targetFor(family, 'usage') },
  ];
}

function objectGroup(objectType: { id: string; name: string }): DataIndexExportObjectGroup {
  const objectTypeId = Number(objectType.id);

  return {
    objectTypeName: objectType.name,
    attributes: choicePair('Attributes', { family: 'attributes', scope: { kind: 'object', objectTypeId } }),
    relationships: choicePair('Relationship Attributes', {
      family: 'attributes',
      scope: { kind: 'relationship', objectTypeId },
    }),
  };
}

/**
 * The whole menu. Object exports are a paid capability, so a workspace that
 * cannot use objects is not offered the object types it does not have.
 */
export function dataIndexExportMenu(
  objectTypes: readonly { id: string; name: string }[],
  { withObjects }: { withObjects: boolean },
): DataIndexExportMenu {
  return {
    profile: choicePair('Attributes', { family: 'attributes', scope: { kind: 'profile' } }),
    objects: withObjects ? objectTypes.map(objectGroup) : [],
    events: choicePair('Events', { family: 'events' }),
  };
}
